import * as THREE from 'three';
import { StaticObject } from './StaticObject';
import { LODLevel } from '../../optimization/LODSystem';

/**
 * Object type enum for static objects
 */
export enum StaticObjectType {
  TREE,
  ROCK,
  BUSH,
  LOAF
}

/**
 * Configuration for LOD static objects
 */
export interface LODStaticObjectConfig {
  type: StaticObjectType;
  name?: string;
  baseScale?: THREE.Vector3;
  materialOverrides?: {
    high?: THREE.MeshStandardMaterial;
    medium?: THREE.MeshStandardMaterial;
    low?: THREE.MeshStandardMaterial;
  };
}

/**
 * A more efficient implementation of static objects with LOD support
 * This class optimizes LOD transitions and reduces the number of matrix updates
 */
export class LODStaticObject extends StaticObject {
  // Track instance counts per object type
  private static instanceCounts: Map<StaticObjectType, number> = new Map([
    [StaticObjectType.TREE, 0],
    [StaticObjectType.ROCK, 0],
    [StaticObjectType.BUSH, 0],
    [StaticObjectType.LOAF, 0]
  ]);
  
  // Track initialization state per object type
  private static initialized: Map<StaticObjectType, boolean> = new Map([
    [StaticObjectType.TREE, false],
    [StaticObjectType.ROCK, false],
    [StaticObjectType.BUSH, false],
    [StaticObjectType.LOAF, false]
  ]);
  
  // Store geometries for different LOD levels and object types
  private static geometries: Map<StaticObjectType, Map<LODLevel, THREE.BufferGeometry>> = new Map([
    [StaticObjectType.TREE, new Map()],
    [StaticObjectType.ROCK, new Map()],
    [StaticObjectType.BUSH, new Map()],
    [StaticObjectType.LOAF, new Map()]
  ]);
  
  // Store materials for different LOD levels and object types
  private static materials: Map<StaticObjectType, Map<LODLevel, THREE.Material>> = new Map([
    [StaticObjectType.TREE, new Map()],
    [StaticObjectType.ROCK, new Map()],
    [StaticObjectType.BUSH, new Map()],
    [StaticObjectType.LOAF, new Map()]
  ]);
  
  // Cache matrices and vectors to reduce GC pressure
  private static readonly matrixCache: THREE.Matrix4 = new THREE.Matrix4();
  private static readonly quaternionCache: THREE.Quaternion = new THREE.Quaternion();
  private static readonly positionCache: THREE.Vector3 = new THREE.Vector3();
  private static readonly scaleCache: THREE.Vector3 = new THREE.Vector3();
  private static readonly zeroScaleMatrix: THREE.Matrix4 = new THREE.Matrix4().makeScale(0, 0, 0);
  
  // Object-specific properties
  private objectType: StaticObjectType;
  private visibleLODLevel: LODLevel = LODLevel.HIGH;
  private baseScale: THREE.Vector3;
  
  // Optimization: keep track of instance details
  private instanceName: string = '';
  private lastTransitionTime: number = 0;
  private transitionInProgress: boolean = false;
  
  // Keep track of the visibility state in each LOD level
  private visibilityState: Map<LODLevel, boolean> = new Map([
    [LODLevel.HIGH, false],
    [LODLevel.MEDIUM, false],
    [LODLevel.LOW, false]
  ]);
  
  constructor(config: LODStaticObjectConfig) {
    // Default name based on object type if not provided
    const name = config.name || `LOD${StaticObjectType[config.type]}`;
    super(name);
    
    this.objectType = config.type;
    this.baseScale = config.baseScale || new THREE.Vector3(1, 1, 1);
    
    // Initialize resources if needed
    if (!LODStaticObject.initialized.get(this.objectType)) {
      this.initializeResources(config);
      LODStaticObject.initialized.set(this.objectType, true);
    }
    
    // Apply base scale according to object type
    this.setScale(this.baseScale.x, this.baseScale.y, this.baseScale.z);
    
    // Get instance ID and set up instancing for high detail level initially
    const instanceId = LODStaticObject.instanceCounts.get(this.objectType)!;
    LODStaticObject.instanceCounts.set(this.objectType, instanceId + 1);
    
    // Create the instance name and set initial visibility
    this.instanceName = this.getInstanceName(LODLevel.HIGH);
    this.setInstance(this.instanceName, instanceId);
    this.visibilityState.set(LODLevel.HIGH, true);
    
    // Enable LOD (will register with LOD system)
    this.enableLOD();
  }
  
  /**
   * Initialize resources for this object type
   */
  private initializeResources(config: LODStaticObjectConfig): void {
    const type = this.objectType;
    
    // Create geometries based on object type
    this.createGeometries(type);
    
    // Create base materials based on object type
    this.createMaterials(type, config.materialOverrides);
    
    // Create instanced meshes for each LOD level - one per object type per LOD level
    this.createInstancedMeshes(type);
  }
  
  /**
   * Create geometries for different LOD levels
   */
  private createGeometries(type: StaticObjectType): void {
    // Use simpler geometries for better performance
    let highDetailLevel = 4; // Was 8
    let mediumDetailLevel = 2; // Was 4
    let lowDetailLevel = 1; // Was 1
    
    switch (type) {
      case StaticObjectType.TREE:
        // Create custom tree geometry with a trunk and crown
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.HIGH, 
          this.createTreeGeometry(8) // High detail tree
        );
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.MEDIUM, 
          this.createTreeGeometry(6) // Medium detail tree
        );
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.LOW, 
          this.createTreeGeometry(4) // Low detail tree
        );
        break;
        
      case StaticObjectType.ROCK:
        // Create custom rock geometry with mix of box and sphere shapes
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.HIGH, 
          this.createRockGeometry(8) // High detail rock
        );
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.MEDIUM, 
          this.createRockGeometry(6) // Medium detail rock
        );
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.LOW, 
          this.createRockGeometry(4) // Low detail rock
        );
        break;
        
      case StaticObjectType.BUSH:
        // Create custom bush geometry with multiple spheres
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.HIGH, 
          this.createBushGeometry(8) // High detail bush
        );
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.MEDIUM, 
          this.createBushGeometry(6) // Medium detail bush
        );
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.LOW, 
          this.createBushGeometry(4) // Low detail bush
        );
        break;

      case StaticObjectType.LOAF:
        // Create custom loaf geometry with stack of toast slices
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.HIGH, 
          this.createLoafGeometry(8) // High detail loaf
        );
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.MEDIUM, 
          this.createLoafGeometry(6) // Medium detail loaf
        );
        LODStaticObject.geometries.get(type)!.set(
          LODLevel.LOW, 
          this.createLoafGeometry(4) // Low detail loaf
        );
        break;
    }
  }
  
  /**
   * Creates a custom rock geometry made of a mix of box and sphere shapes
   */
  private createRockGeometry(segments: number): THREE.BufferGeometry {
    // Create a composite geometry
    const geometry = new THREE.BufferGeometry();
    
    // Define the shapes for the rock with type annotations
    interface RockShape {
      type: 'sphere' | 'box';
      radius?: number;
      width?: number;
      height?: number;
      depth?: number;
      x: number;
      y: number;
      z: number;
      rotation: [number, number, number];
    }
    
    const shapes: RockShape[] = [
      // Main central sphere
      { type: 'sphere', radius: 0.5, x: 0, y: 0, z: 0, rotation: [0, 0, 0] },
      
      // Add box shapes in different positions, rotations, and sizes
      { type: 'box', width: 0.5, height: 0.4, depth: 0.5, x: 0.3, y: 0.1, z: 0.2, rotation: [0.3, 0.5, 0.2] },
      { type: 'box', width: 0.4, height: 0.3, depth: 0.5, x: -0.25, y: 0.15, z: 0.1, rotation: [0.1, -0.3, 0.1] },
      { type: 'box', width: 0.5, height: 0.4, depth: 0.3, x: 0.1, y: -0.2, z: -0.3, rotation: [0.2, 0.1, -0.3] },
      
      // Add some smaller spheres for more organic look
      { type: 'sphere', radius: 0.25, x: -0.35, y: -0.1, z: -0.2, rotation: [0, 0, 0] },
      { type: 'sphere', radius: 0.2, x: 0.4, y: -0.25, z: 0, rotation: [0, 0, 0] }
    ];
    
    // For medium and low detail, use fewer shapes
    if (segments <= 6) {
      shapes.length = 4; // Remove the last two shapes
    }
    if (segments <= 4) {
      shapes.length = 3; // Only keep the main shapes
    }
    
    // Create and combine all the geometries
    let combinedPositions: number[] = [];
    let combinedNormals: number[] = [];
    let combinedIndices: number[] = [];
    let indexOffset = 0;
    
    for (const shape of shapes) {
      let shapeGeometry: THREE.BufferGeometry;
      
      if (shape.type === 'sphere' && shape.radius) {
        // Create a sphere with the specified detail level
        shapeGeometry = new THREE.SphereGeometry(
          shape.radius, 
          segments, 
          segments
        );
        
        // Deform the sphere to make it less perfect
        this.deformSphereGeometry(shapeGeometry, shape.radius);
      } else if (shape.type === 'box' && shape.width && shape.height && shape.depth) {
        // Create a box with the specified detail level
        shapeGeometry = new THREE.BoxGeometry(
          shape.width,
          shape.height,
          shape.depth,
          Math.max(2, segments / 2),
          Math.max(2, segments / 2),
          Math.max(2, segments / 2)
        );
        
        // Apply some vertex displacement to make it less perfect
        this.deformBoxGeometry(shapeGeometry, 0.1);
      } else {
        // Skip this shape if missing parameters
        continue;
      }
      
      // Apply rotation to the geometry
      if (shape.rotation) {
        const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(
          new THREE.Euler(shape.rotation[0], shape.rotation[1], shape.rotation[2])
        );
        shapeGeometry.applyMatrix4(rotationMatrix);
      }
      
      // Get attributes
      const positions = Array.from(shapeGeometry.getAttribute('position').array);
      const normals = Array.from(shapeGeometry.getAttribute('normal').array);
      
      // Get indices safely
      let indices: number[] = [];
      const indexAttribute = shapeGeometry.getIndex();
      if (indexAttribute) {
        indices = Array.from(indexAttribute.array);
      }
      
      // Transform positions to place the shape correctly
      for (let i = 0; i < positions.length; i += 3) {
        positions[i] += shape.x;
        positions[i + 1] += shape.y;
        positions[i + 2] += shape.z;
      }
      
      // Add to combined arrays with adjusted indices
      combinedPositions.push(...positions);
      combinedNormals.push(...normals);
      
      // Adjust indices to account for merged vertices
      for (let i = 0; i < indices.length; i++) {
        combinedIndices.push(indices[i] + indexOffset);
      }
      
      // Update offset for next shape
      indexOffset += positions.length / 3;
    }
    
    // Set attributes on the combined geometry
    geometry.setAttribute(
      'position', 
      new THREE.Float32BufferAttribute(combinedPositions, 3)
    );
    geometry.setAttribute(
      'normal', 
      new THREE.Float32BufferAttribute(combinedNormals, 3)
    );
    geometry.setIndex(combinedIndices);
    
    return geometry;
  }
  
  /**
   * Apply random displacement to box vertices for more natural rock look
   */
  private deformBoxGeometry(geometry: THREE.BufferGeometry, intensity: number): void {
    const positionAttribute = geometry.getAttribute('position');
    const positions = positionAttribute.array;
    
    // Apply random displacement to each vertex
    for (let i = 0; i < positions.length; i += 3) {
      // Apply random displacement in x, y, z directions
      positions[i] += (Math.random() - 0.5) * intensity * 2;
      positions[i + 1] += (Math.random() - 0.5) * intensity * 2;
      positions[i + 2] += (Math.random() - 0.5) * intensity * 2;
    }
    
    // Update normals for correct lighting
    geometry.computeVertexNormals();
  }
  
  /**
   * Creates a custom bush geometry made of several spheres
   */
  private createBushGeometry(segments: number): THREE.BufferGeometry {
    // Create a composite geometry
    const geometry = new THREE.BufferGeometry();
    
    // Define the spheres based on segment detail level
    const spheres = [
      { radius: 0.5, x: 0, y: 0.0, z: 0 },      // Main sphere
      { radius: 0.4, x: 0.4, y: -0.1, z: 0.3 },  // Right side
      { radius: 0.4, x: -0.4, y: -0.1, z: 0.3 }, // Left side
      { radius: 0.4, x: 0, y: 0.1, z: 0.4 },     // Front 
      { radius: 0.4, x: 0, y: 0.1, z: -0.4 },    // Back
    ];
    
    // Add a couple more spheres for higher quality shadows in higher LOD levels
    if (segments >= 6) {
      // Add lower spheres for better ground shadow
      spheres.push(
        { radius: 0.4, x: 0.3, y: -0.4, z: 0 },      // Bottom right
        { radius: 0.4, x: -0.3, y: -0.4, z: 0 }      // Bottom left
      );
    }
    
    // Fewer spheres for lower detail levels
    if (segments <= 4) {
      // Use only 3 spheres for low LOD
      spheres.length = 3;
    }
    
    // Create and combine all the sphere geometries
    let combinedPositions: number[] = [];
    let combinedNormals: number[] = [];
    let combinedIndices: number[] = [];
    let indexOffset = 0;
    
    for (const sphere of spheres) {
      // Create a sphere with the specified detail level
      const sphereGeometry = new THREE.SphereGeometry(
        sphere.radius, 
        segments, 
        segments
      );
      
      // Move vertices for more natural looking shape
      this.deformSphereGeometry(sphereGeometry, sphere.radius);
      
      // Get attributes
      const positions = Array.from(sphereGeometry.getAttribute('position').array);
      const normals = Array.from(sphereGeometry.getAttribute('normal').array);
      const indices = Array.from(sphereGeometry.getIndex()?.array || []);
      
      // Transform positions to place the sphere correctly
      for (let i = 0; i < positions.length; i += 3) {
        positions[i] += sphere.x;
        positions[i + 1] += sphere.y;
        positions[i + 2] += sphere.z;
      }
      
      // Add to combined arrays with adjusted indices
      combinedPositions.push(...positions);
      combinedNormals.push(...normals);
      
      // Adjust indices to account for merged vertices
      for (let i = 0; i < indices.length; i++) {
        combinedIndices.push(indices[i] + indexOffset);
      }
      
      // Update offset for next sphere
      indexOffset += positions.length / 3;
    }
    
    // Set attributes on the combined geometry
    geometry.setAttribute(
      'position', 
      new THREE.Float32BufferAttribute(combinedPositions, 3)
    );
    geometry.setAttribute(
      'normal', 
      new THREE.Float32BufferAttribute(combinedNormals, 3)
    );
    geometry.setIndex(combinedIndices);
    
    return geometry;
  }
  
  /**
   * Apply random displacement to sphere vertices for more natural look
   */
  private deformSphereGeometry(geometry: THREE.BufferGeometry, radius: number): void {
    const positionAttribute = geometry.getAttribute('position');
    const positions = positionAttribute.array;
    const intensity = 0.15; // Deformation intensity
    
    // Apply random displacement to each vertex
    for (let i = 0; i < positions.length; i += 3) {
      // Skip vertices at the very top/bottom of the sphere
      const y = positions[i + 1];
      if (Math.abs(y) > radius * 0.9) continue;
      
      // Apply random displacement in x, y, z directions
      positions[i] += (Math.random() - 0.5) * intensity * radius;
      positions[i + 1] += (Math.random() - 0.5) * intensity * radius;
      positions[i + 2] += (Math.random() - 0.5) * intensity * radius;
    }
    
    // Update normals for correct lighting
    geometry.computeVertexNormals();
  }
  
  /**
   * Create materials for different LOD levels
   */
  private createMaterials(type: StaticObjectType, overrides?: { high?: THREE.MeshStandardMaterial, medium?: THREE.MeshStandardMaterial, low?: THREE.MeshStandardMaterial }): void {
    // Default gray color if no overrides are provided
    const defaultColor = 0xCCCCCC; // Light gray default
    
    // Create high detail material or use override
    const highMaterial = overrides?.high || new THREE.MeshStandardMaterial({
      color: defaultColor,
      roughness: 0.8,
      metalness: 0.1
    });
    
    // Create medium detail material or use override
    const mediumMaterial = overrides?.medium || overrides?.high || new THREE.MeshStandardMaterial({
      color: defaultColor,
      roughness: 0.8,
      metalness: 0.1
    });
    
    // Create low detail material or use override
    const lowMaterial = overrides?.low || overrides?.medium || overrides?.high || new THREE.MeshStandardMaterial({
      color: defaultColor,
      roughness: 0.8,
      metalness: 0.1
    });
    
    // Store materials (with type casting to prevent TS errors)
    LODStaticObject.materials.get(type)!.set(LODLevel.HIGH, highMaterial as THREE.Material);
    LODStaticObject.materials.get(type)!.set(LODLevel.MEDIUM, mediumMaterial as THREE.Material);
    LODStaticObject.materials.get(type)!.set(LODLevel.LOW, lowMaterial as THREE.Material);
  }
  
  /**
   * Create instanced meshes for each LOD level
   */
  private createInstancedMeshes(type: StaticObjectType): void {
    // Maximum number of instances to support - could be made configurable
    const maxInstances = 500;
    
    // Prefix to use for this object type's instance names
    const prefix = this.getTypePrefix(type);
    
    // Create instanced meshes for each LOD level
    const highMesh = StaticObject.getInstancedMesh(
      `${prefix}High`, 
      LODStaticObject.geometries.get(type)!.get(LODLevel.HIGH)!, 
      LODStaticObject.materials.get(type)!.get(LODLevel.HIGH)!, 
      maxInstances
    );
    
    const mediumMesh = StaticObject.getInstancedMesh(
      `${prefix}Medium`, 
      LODStaticObject.geometries.get(type)!.get(LODLevel.MEDIUM)!, 
      LODStaticObject.materials.get(type)!.get(LODLevel.MEDIUM)!, 
      maxInstances
    );
    
    const lowMesh = StaticObject.getInstancedMesh(
      `${prefix}Low`, 
      LODStaticObject.geometries.get(type)!.get(LODLevel.LOW)!, 
      LODStaticObject.materials.get(type)!.get(LODLevel.LOW)!, 
      maxInstances
    );
    
    // Ensure all meshes cast and receive shadows
    // This is especially important for the bush geometry
    highMesh.castShadow = true;
    highMesh.receiveShadow = true;
    mediumMesh.castShadow = true;
    mediumMesh.receiveShadow = true;
    lowMesh.castShadow = true;
    lowMesh.receiveShadow = true;
    
    // Initialize all instances with zero scale to make them invisible by default
    // This prevents the "ghost instance" at the origin (0,0,0)
    const zeroMatrix = LODStaticObject.zeroScaleMatrix;
    for (let i = 0; i < maxInstances; i++) {
      highMesh.setMatrixAt(i, zeroMatrix);
      mediumMesh.setMatrixAt(i, zeroMatrix);
      lowMesh.setMatrixAt(i, zeroMatrix);
    }
    
    // Mark instance matrices as needing update
    highMesh.instanceMatrix.needsUpdate = true;
    mediumMesh.instanceMatrix.needsUpdate = true;
    lowMesh.instanceMatrix.needsUpdate = true;
  }
  
  /**
   * Get the instance name for a given LOD level
   */
  private getInstanceName(level: LODLevel): string {
    const prefix = this.getTypePrefix(this.objectType);
    
    switch (level) {
      case LODLevel.HIGH:
        return `${prefix}High`;
      case LODLevel.MEDIUM:
        return `${prefix}Medium`;
      case LODLevel.LOW:
        return `${prefix}Low`;
      default:
        return `${prefix}Low`;
    }
  }
  
  /**
   * Get the prefix for instance names based on object type
   */
  private getTypePrefix(type: StaticObjectType): string {
    switch (type) {
      case StaticObjectType.TREE:
        return 'lodTree';
      case StaticObjectType.ROCK:
        return 'lodRock';
      case StaticObjectType.BUSH:
        return 'lodBush';
      case StaticObjectType.LOAF:
        return 'lodLoaf';
      default:
        return 'lodObject';
    }
  }
  
  /**
   * Prevent LOD transitions from happening too frequently
   */
  private canTransition(): boolean {
    const now = performance.now();
    // Don't allow transitions more often than every 250ms to prevent flickering
    if (now - this.lastTransitionTime < 250) {
      return false;
    }
    
    return !this.transitionInProgress;
  }
  
  /**
   * Fixed implementation of setLODLevel that prevents flickering
   */
  public override setLODLevel(level: LODLevel): void {
    // If the level hasn't changed, do nothing
    if (this.visibleLODLevel === level) {
      return;
    }
    
    // Prevent too-frequent LOD changes that can cause flickering
    if (!this.canTransition()) {
      return;
    }
    
    // Cache the old level for cleanup
    const oldLevel = this.visibleLODLevel;
    
    // Update our LOD level state
    this.visibleLODLevel = level;
    this.lastTransitionTime = performance.now();
    this.transitionInProgress = true;
    
    try {
      // Get instance ID
      const instanceId = this.getInstanceId();
      
      // First hide the old instance - set its scale to zero
      if (this.visibilityState.get(oldLevel)) {
        const oldInstanceName = this.getInstanceName(oldLevel);
        const oldMesh = StaticObject.instancedMeshes.get(oldInstanceName);
        
        if (oldMesh) {
          oldMesh.setMatrixAt(instanceId, LODStaticObject.zeroScaleMatrix);
          oldMesh.instanceMatrix.needsUpdate = true;
        }
        
        // Update visibility tracking
        this.visibilityState.set(oldLevel, false);
      }
      
      // Now make the new instance visible
      const newInstanceName = this.getInstanceName(level);
      
      // Update the instance type tracking
      this.instanceName = newInstanceName;
      this.setInstance(newInstanceName, instanceId);
      
      // Apply the current transform to the new instance
      this.updateInstanceMatrix();
      
      // Update visibility tracking
      this.visibilityState.set(level, true);
    } finally {
      // Make sure we always reset the transition state
      this.transitionInProgress = false;
    }
  }
  
  /**
   * More efficient implementation of updateInstanceMatrix that reuses cached objects
   */
  protected override updateInstanceMatrix(): void {
    if (this.instanceId === -1 || !this.instanceType) return;
    
    const instancedMesh = StaticObject.instancedMeshes.get(this.instanceType);
    if (!instancedMesh) return;
    
    // OPTIMIZATION: Reuse static vectors/quaternions/matrices to avoid allocations
    LODStaticObject.positionCache.copy(this.getPosition());
    LODStaticObject.quaternionCache.setFromEuler(this.getRotation());
    LODStaticObject.scaleCache.copy(this.getScale());
    
    LODStaticObject.matrixCache.compose(
      LODStaticObject.positionCache,
      LODStaticObject.quaternionCache,
      LODStaticObject.scaleCache
    );
    
    instancedMesh.setMatrixAt(this.instanceId, LODStaticObject.matrixCache);
    instancedMesh.instanceMatrix.needsUpdate = true;
  }
  
  /**
   * Get the current object type
   */
  public getObjectType(): StaticObjectType {
    return this.objectType;
  }
  
  /**
   * Helper method to access protected instanceId member
   */
  private getInstanceId(): number {
    return this.instanceId;
  }
  
  /**
   * Get the current LOD level
   */
  public override getLODLevel(): LODLevel {
    return this.visibleLODLevel;
  }

  /**
   * Creates a custom tree geometry with a trunk and low-poly crown
   */
  private createTreeGeometry(segments: number): THREE.BufferGeometry {
    // Create a composite geometry
    const geometry = new THREE.BufferGeometry();
    
    // Define the shapes for the tree with type annotations
    interface TreeShape {
      type: 'cylinder' | 'cone' | 'box';
      radiusTop?: number;
      radiusBottom?: number;
      height?: number;
      width?: number;
      depth?: number;
      x: number;
      y: number;
      z: number;
      rotation: [number, number, number];
      isTrunk?: boolean; // Flag to identify trunk parts for material assignment
    }
    
    const shapes: TreeShape[] = [
      // Main trunk (cylinder)
      { 
        type: 'cylinder', 
        radiusTop: 0.15, 
        radiusBottom: 0.25, 
        height: 0.8, 
        x: 0, 
        y: 0, 
        z: 0, 
        rotation: [0, 0, 0],
        isTrunk: true
      },
      
      // Tree crown parts (cones at different levels)
      { 
        type: 'cone', 
        radiusBottom: 0.6, 
        radiusTop: 0.1, 
        height: 1.2, 
        x: 0, 
        y: 1.0, 
        z: 0, 
        rotation: [0, 0, 0],
        isTrunk: false
      },
      { 
        type: 'cone', 
        radiusBottom: 0.5, 
        radiusTop: 0.0, 
        height: 1.0, 
        x: 0, 
        y: 1.9, 
        z: 0, 
        rotation: [0, 0, 0],
        isTrunk: false
      },
      { 
        type: 'cone', 
        radiusBottom: 0.4, 
        radiusTop: 0.0, 
        height: 0.7, 
        x: 0, 
        y: 2.5, 
        z: 0, 
        rotation: [0, 0, 0],
        isTrunk: false
      },
    ];
    
    // For medium and low detail, use fewer cone sections
    if (segments <= 6) {
      shapes.length = 3; // Remove the top cone
    }
    if (segments <= 4) {
      shapes.length = 2; // Only keep trunk and main cone
    }
    
    // Create and combine all the geometries
    let combinedPositions: number[] = [];
    let combinedNormals: number[] = [];
    let combinedIndices: number[] = [];
    let combinedGroups: { start: number, count: number, materialIndex: number }[] = [];
    let indexOffset = 0;
    
    for (const shape of shapes) {
      let shapeGeometry: THREE.BufferGeometry;
      
      if (shape.type === 'cylinder' && shape.radiusTop !== undefined && shape.radiusBottom !== undefined && shape.height) {
        // Create a cylinder for the trunk
        shapeGeometry = new THREE.CylinderGeometry(
          shape.radiusTop,
          shape.radiusBottom,
          shape.height,
          Math.max(4, segments),  // Radial segments
          Math.max(1, segments / 2)  // Height segments
        );
        
        // Apply some vertex displacement for a more natural look
        this.deformCylinderGeometry(shapeGeometry, 0.05);
      } 
      else if (shape.type === 'cone' && shape.radiusBottom !== undefined && shape.radiusTop !== undefined && shape.height) {
        // Create a cone for the tree crown sections
        shapeGeometry = new THREE.CylinderGeometry(
          shape.radiusTop,
          shape.radiusBottom,
          shape.height,
          Math.max(4, segments),  // Radial segments
          Math.max(1, segments / 2)  // Height segments
        );
        
        // Apply some vertex displacement for a more natural look
        this.deformCylinderGeometry(shapeGeometry, 0.1);
      }
      else {
        // Skip this shape if missing parameters
        continue;
      }
      
      // Apply rotation to the geometry
      if (shape.rotation) {
        const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(
          new THREE.Euler(shape.rotation[0], shape.rotation[1], shape.rotation[2])
        );
        shapeGeometry.applyMatrix4(rotationMatrix);
      }
      
      // Get attributes
      const positions = Array.from(shapeGeometry.getAttribute('position').array);
      const normals = Array.from(shapeGeometry.getAttribute('normal').array);
      
      // Get indices safely
      let indices: number[] = [];
      const indexAttribute = shapeGeometry.getIndex();
      if (indexAttribute) {
        indices = Array.from(indexAttribute.array);
      }
      
      // Transform positions to place the shape correctly
      for (let i = 0; i < positions.length; i += 3) {
        positions[i] += shape.x;
        positions[i + 1] += shape.y;
        positions[i + 2] += shape.z;
      }
      
      // Add to combined arrays with adjusted indices
      combinedPositions.push(...positions);
      combinedNormals.push(...normals);
      
      // Adjust indices to account for merged vertices
      for (let i = 0; i < indices.length; i++) {
        combinedIndices.push(indices[i] + indexOffset);
      }
      
      // Create groups for material assignment - trunk is material 0, crown is material 1
      const materialIndex = shape.isTrunk ? 0 : 1;
      combinedGroups.push({
        start: combinedIndices.length - indices.length,
        count: indices.length,
        materialIndex: materialIndex
      });
      
      // Update offset for next shape
      indexOffset += positions.length / 3;
    }
    
    // Set attributes on the combined geometry
    geometry.setAttribute(
      'position', 
      new THREE.Float32BufferAttribute(combinedPositions, 3)
    );
    geometry.setAttribute(
      'normal', 
      new THREE.Float32BufferAttribute(combinedNormals, 3)
    );
    geometry.setIndex(combinedIndices);
    
    // Set material groups
    for (const group of combinedGroups) {
      geometry.addGroup(group.start, group.count, group.materialIndex);
    }
    
    return geometry;
  }

  /**
   * Apply random displacement to cylinder/cone vertices for more natural look
   */
  private deformCylinderGeometry(geometry: THREE.BufferGeometry, intensity: number): void {
    const positionAttribute = geometry.getAttribute('position');
    const positions = positionAttribute.array;
    
    // Apply random displacement to each vertex
    for (let i = 0; i < positions.length; i += 3) {
      // Apply random displacement in x and z directions (not y to preserve height)
      positions[i] += (Math.random() - 0.5) * intensity * 2;
      // Skip y displacement to maintain height
      positions[i + 2] += (Math.random() - 0.5) * intensity * 2;
    }
    
    // Update normals for correct lighting
    geometry.computeVertexNormals();
  }

  /**
   * Creates a custom loaf geometry as a stack of toast slices
   * Each slice is slightly offset from the others for an uneven stack appearance
   */
  private createLoafGeometry(segments: number): THREE.BufferGeometry {
    // Create a composite geometry
    const geometry = new THREE.BufferGeometry();
    
    // Define the shapes for the toast slices
    interface ToastSlice {
      width: number;
      height: number;
      depth: number;
      x: number;
      y: number;
      z: number;
      rotation: [number, number, number];
    }
    
    // Each slice is 0.5 units high as requested
    const sliceHeight = 0.3;
    
    // Create 10 slices of toast stacked on top of each other
    const numSlices = 40;
    const slices: ToastSlice[] = [];
    
    // Create the stack of toast slices with random offsets for an uneven stack
    let currentHeight = 0;
    
    for (let i = 0; i < numSlices; i++) {
      // Each slice has a slightly different size
      const width = 2.8 + Math.random() * 0.4;  // 1.8-2.2 units wide
      const depth = 2.8 + Math.random() * 0.4;  // 1.8-2.2 units deep
      
      // Small random offsets for x and z for uneven stacking
      const offsetX = (Math.random() - 0.5) * 0.4;  // -0.2 to 0.2
      const offsetZ = (Math.random() - 0.5) * 0.4;  // -0.2 to 0.2
      
      // Small random rotation for each slice (in radians)
      const rotY = (Math.random() - 0.5) * 0.3;  // -0.15 to 0.15 radians
      
      // Add the slice to the stack
      slices.push({
        width: width,
        height: sliceHeight,
        depth: depth,
        x: offsetX,
        y: currentHeight + sliceHeight/2, // Position based on current height (center of slice)
        z: offsetZ,
        rotation: [0, rotY, 0]
      });
      
      // Increment height for the next slice
      currentHeight += sliceHeight;
    }
    
    // Create and combine all the geometries
    let combinedPositions: number[] = [];
    let combinedNormals: number[] = [];
    let combinedIndices: number[] = [];
    let indexOffset = 0;
    
    for (const slice of slices) {
      // Create a box geometry for each slice
      const sliceGeometry = new THREE.BoxGeometry(
        slice.width,
        slice.height,
        slice.depth,
        Math.max(2, Math.floor(segments / 2)), // Horizontal segments
        1,  // Only 1 segment for height
        Math.max(2, Math.floor(segments / 2))  // Depth segments
      );
      
      // Apply some vertex displacement for a more natural look (crust edges)
      this.deformToastGeometry(sliceGeometry, 0.05);
      
      // Apply rotation to the geometry
      if (slice.rotation) {
        const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(
          new THREE.Euler(slice.rotation[0], slice.rotation[1], slice.rotation[2])
        );
        sliceGeometry.applyMatrix4(rotationMatrix);
      }
      
      // Get attributes
      const positions = Array.from(sliceGeometry.getAttribute('position').array);
      const normals = Array.from(sliceGeometry.getAttribute('normal').array);
      
      // Get indices safely
      let indices: number[] = [];
      const indexAttribute = sliceGeometry.getIndex();
      if (indexAttribute) {
        indices = Array.from(indexAttribute.array);
      }
      
      // Transform positions to place the slice correctly
      for (let i = 0; i < positions.length; i += 3) {
        positions[i] += slice.x;
        positions[i + 1] += slice.y;
        positions[i + 2] += slice.z;
      }
      
      // Add to combined arrays with adjusted indices
      combinedPositions.push(...positions);
      combinedNormals.push(...normals);
      
      // Adjust indices to account for merged vertices
      for (let i = 0; i < indices.length; i++) {
        combinedIndices.push(indices[i] + indexOffset);
      }
      
      // Update offset for next slice
      indexOffset += positions.length / 3;
    }
    
    // Set attributes on the combined geometry
    geometry.setAttribute(
      'position', 
      new THREE.Float32BufferAttribute(combinedPositions, 3)
    );
    geometry.setAttribute(
      'normal', 
      new THREE.Float32BufferAttribute(combinedNormals, 3)
    );
    geometry.setIndex(combinedIndices);
    
    return geometry;
  }

  /**
   * Apply random displacement to toast slice vertices for more natural look
   * Focuses on creating crispy/crusty edges while keeping flat surfaces
   */
  private deformToastGeometry(geometry: THREE.BufferGeometry, intensity: number): void {
    const positionAttribute = geometry.getAttribute('position');
    const positions = positionAttribute.array;
    
    // Apply random displacement mainly to the edges
    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      const z = positions[i + 2];
      
      // Calculate the distance from the center in xz plane (horizontal)
      const distFromCenter = Math.sqrt(x*x + z*z);
      
      // Apply more displacement to the edges
      if (Math.abs(y) > 0.2 || distFromCenter > 0.7) {
        // Edge vertices get more displacement for a crusty look
        positions[i] += (Math.random() - 0.5) * intensity * 3;
        positions[i + 1] += (Math.random() - 0.5) * intensity * 3;
        positions[i + 2] += (Math.random() - 0.5) * intensity * 3;
      } else {
        // Interior vertices get minimal displacement
        positions[i] += (Math.random() - 0.5) * intensity * 0.5;
        positions[i + 1] += (Math.random() - 0.5) * intensity * 0.5;
        positions[i + 2] += (Math.random() - 0.5) * intensity * 0.5;
      }
    }
    
    // Update normals for correct lighting
    geometry.computeVertexNormals();
  }
} 